// П7 (fd-onboard-25, 26, 27, 30): разделы анкеты продавца - решение по каждому.
//
// «О магазине» и «Реквизиты» модератор принимает или возвращает на доработку
// кнопками здесь. «Документы» следуют за решениями по каждому файлу в «личном
// деле» ниже; принять раздел целиком можно для документов старой схемы, у
// которых нет строк в «личном деле». Магазин одобряется только когда приняты
// все три раздела - последний принятый раздел одобряет его сам.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, PencilLine, Undo2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api, type ProfileSectionKey, type ProfileSectionState } from "@/api";
import { sellerKeys, useSellerSections } from "@/api/hooks/use-sellers";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { cn } from "@/lib/utils";
const STATUS_STYLE: Record<ProfileSectionState["status"], string> = {
  draft: "border border-dashed text-muted-foreground",
  submitted: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  accepted: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  rework: "bg-destructive/15 text-destructive"
};
const DEFAULTS = {
  title: "Разделы анкеты",
  subtitle: "Каждый раздел проверяется отдельно. Магазин одобряется, когда приняты все три.",
  about: "О магазине",
  requisites: "Реквизиты",
  documents: "Документы",
  draft: "Не отправлен",
  submitted: "На проверке",
  accepted: "Принят",
  rework: "На доработке",
  changed: "Изменён после проверки",
  accept: "Принять",
  rework_action: "На доработку",
  documentsHint: "Решение - по каждому документу в «Личном деле» ниже.",
  reworkTitle: "Раздел «{{section}}» на доработку",
  reworkBody: "Продавец увидит замечание у раздела и получит уведомление. После исправления на проверку вернётся только этот раздел.",
  remarkPlaceholder: "Что нужно исправить",
  remarkRequired: "Укажите, что нужно исправить",
  deadline: "Срок исправления"
};
export function SellerSectionsPanel({
  userId,
  canModerate
}: {
  userId: string;
  /** Решения доступны, пока заявка на проверке или магазин работает (правки профиля). */
  canModerate: boolean;
}) {
  const {
    t
  } = useTranslation();
  const tt = (key: keyof typeof DEFAULTS, opts?: Record<string, string>) => t(`p7.sections.${key}`, {
    defaultValue: DEFAULTS[key],
    ...opts
  });
  const queryClient = useQueryClient();
  const query = useSellerSections(userId);
  const [busy, setBusy] = useState<ProfileSectionKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reworkFor, setReworkFor] = useState<ProfileSectionKey | null>(null);
  const [remark, setRemark] = useState("");
  const [deadline, setDeadline] = useState("");
  const refresh = async () => {
    // Принятие последнего раздела одобряет магазин - перечитываем всё о продавце.
    await queryClient.invalidateQueries({
      queryKey: sellerKeys.all
    });
    await queryClient.invalidateQueries({
      queryKey: ["applications"]
    });
  };
  const accept = async (section: ProfileSectionKey) => {
    setBusy(section);
    setError(null);
    try {
      queryClient.setQueryData(sellerKeys.sections(userId), await api.sellers.reviewSection(userId, section, {
        status: "accepted"
      }));
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const sendRework = async () => {
    if (!reworkFor) return;
    if (!remark.trim()) {
      setError(tt("remarkRequired"));
      return;
    }
    setBusy(reworkFor);
    setError(null);
    try {
      queryClient.setQueryData(sellerKeys.sections(userId), await api.sellers.reviewSection(userId, reworkFor, {
        status: "rework",
        remark: remark.trim(),
        deadline: deadline || null
      }));
      setReworkFor(null);
      setRemark("");
      setDeadline("");
      await refresh();
    } catch (e_0) {
      setError((e_0 as Error).message);
    } finally {
      setBusy(null);
    }
  };
  return <Card data-testid="seller-sections">
			<CardHeader className="space-y-1 pb-2">
				<CardTitle className="text-base">{tt("title")}</CardTitle>
				<p className="text-sm text-muted-foreground">{tt("subtitle")}</p>
			</CardHeader>
			<CardContent className="flex flex-col gap-3 text-sm">
				{query.error && <p className="text-destructive">{query.error.message}</p>}
				{error && !reworkFor && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-destructive">{error}</div>}
				{query.data?.map(item => <div key={item.section} data-testid={`seller-section-${item.section}`} className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-start md:justify-between">
						<div className="min-w-0 space-y-1">
							<div className="flex flex-wrap items-center gap-2">
								<span className="font-medium">{tt(item.section)}</span>
								<span className={cn("rounded-full px-2 py-0.5 text-xs", STATUS_STYLE[item.status])}>
									{tt(item.status)}
								</span>
								{item.changedAfterReview && item.status === "submitted" && <Badge variant="outline" className="gap-1 text-xs" data-testid="section-changed">
										<PencilLine className="size-3" />
										{tt("changed")}
									</Badge>}
							</div>
							{item.status === "rework" && item.remark && <p className="whitespace-pre-line text-xs text-destructive">{item.remark}</p>}
							{item.section === "documents" && <p className="text-xs text-muted-foreground">{tt("documentsHint")}</p>}
						</div>
						{canModerate && item.status !== "draft" && <div className="flex shrink-0 gap-2">
								{/* «Документы» на доработке принимают по каждому файлу, не разделом. */}
								{item.status !== "accepted" && !(item.section === "documents" && item.status === "rework") && <Button size="sm" disabled={busy !== null} onClick={() => void accept(item.section)}>
										<Check className="size-4" />
										{tt("accept")}
									</Button>}
								{item.section !== "documents" && item.status !== "rework" && <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => {
            setError(null);
            setReworkFor(item.section);
          }}>
										<Undo2 className="size-4" />
										{tt("rework_action")}
									</Button>}
							</div>}
					</div>)}
			</CardContent>

			<ConfirmDialog open={reworkFor !== null} busy={busy !== null} title={reworkFor ? tt("reworkTitle", {
      section: tt(reworkFor)
    }) : ""} description={tt("reworkBody")} confirmLabel={tt("rework_action")} onConfirm={sendRework} onClose={() => {
      setReworkFor(null);
      setRemark("");
      setDeadline("");
      setError(null);
    }}>
				<div className="flex flex-col gap-3">
					<textarea value={remark} onChange={e_1 => setRemark(e_1.target.value)} placeholder={tt("remarkPlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
					<label className="flex flex-col gap-1 text-sm">
						<span className="text-muted-foreground">{tt("deadline")}</span>
						<input type="date" min={new Date().toISOString().slice(0, 10)} value={deadline} onChange={e_2 => setDeadline(e_2.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" />
					</label>
					{error && <span className="text-sm text-destructive">{error}</span>}
				</div>
			</ConfirmDialog>
		</Card>;
}

/** Все ли разделы приняты - кнопка «Одобрить» без этого неактивна. */
export const allSectionsAccepted = (sections?: ProfileSectionState[]): boolean => Boolean(sections?.length) && (sections ?? []).every(s => s.status === "accepted");